import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import {
  TrendingUp,
  ShieldCheck,
  Truck,
  BarChart3,
  Users,
  Zap,
  Star,
  ArrowRight,
  CheckCircle2,
  Package,
  Globe,
  BadgeIndianRupee,
  HeartHandshake,
  Microscope,
  Leaf,
} from "lucide-react";

// const STATS = [
//   { val: "50K+", label: "Active Buyers", icon: Users },
//   { val: "₹2Cr+", label: "Monthly GMV", icon: BadgeIndianRupee },
//   { val: "1200+", label: "Products Listed", icon: Package },
//   { val: "98%", label: "Seller Rating", icon: Star },
// ];

const BENEFITS = [
  {
    icon: Globe,
    title: "Pan-India Reach",
    desc: "Sell your bioscience products to researchers, hospitals and labs across all 28 states with a single listing.",
    color: "text-indigo-600",
    bg: "bg-indigo-50",
  },
  {
    icon: BadgeIndianRupee,
    title: "Highest Margins",
    desc: "Keep up to 90% of your revenue. Our transparent pricing model means zero hidden charges, ever.",
    color: "text-cyan-600",
    bg: "bg-cyan-50",
  },
  {
    icon: ShieldCheck,
    title: "Secure Payments",
    desc: "Get paid within 7 working days directly to your bank account. GST-compliant invoices generated automatically.",
    color: "text-emerald-600",
    bg: "bg-emerald-50",
  },
  {
    icon: BarChart3,
    title: "Real-time Analytics",
    desc: "Track sales, impressions, conversion rates and inventory health from your seller dashboard.",
    color: "text-amber-600",
    bg: "bg-amber-50",
  },
  {
    icon: Truck,
    title: "Logistics Support",
    desc: "Partner with our certified cold-chain logistics partners for safe, temperature-controlled deliveries.",
    color: "text-rose-600",
    bg: "bg-rose-50",
  },
  {
    icon: HeartHandshake,
    title: "Dedicated Support",
    desc: "A dedicated key account manager assigned for sellers with ₹5L+ monthly revenue.",
    color: "text-purple-600",
    bg: "bg-purple-50",
  },
];

const STEPS = [
  {
    num: "01",
    title: "Create Account",
    desc: "Register with basic details and verify your email address.",
  },
  {
    num: "02",
    title: "Verify GST",
    desc: "Enter your GSTIN to validate your business identity instantly.",
  },
  {
    num: "03",
    title: "Complete KYC",
    desc: "Submit PAN, Aadhaar, electricity bill and business address.",
  },
  {
    num: "04",
    title: "List Products",
    desc: "Add your bioscience products and go live to thousands of buyers.",
  },
];

const TESTIMONIALS = [
  {
    name: "Dr. Priya Sharma",
    role: "Founder, BioLab Supplies",
    quote:
      "Within 3 months of joining Harappa bioscience Seller platform, our revenue tripled. The platform is incredibly intuitive.",
    rating: 5,
  },
  {
    name: "Rahul Mehta",
    role: "Partner, GeneTech Solutions",
    quote:
      "The GST verification was instant and KYC took less than 10 minutes. Best onboarding experience in the industry.",
    rating: 5,
  },
  {
    name: "Dr. Ananya Krishnan",
    role: "CEO, PharmaCore India",
    quote:
      "Our diagnostics kits now reach labs in tier-2 cities we never thought possible. Logistics support is top-notch.",
    rating: 5,
  },
];

const StarRating: React.FC<{ count: number }> = ({ count }) => (
  <div className="flex gap-1 mb-4">
    {Array.from({ length: count }).map((_, i) => (
      <Star key={i} size={14} className="fill-amber-400 text-amber-400" />
    ))}
  </div>
);

const HomePage = () => {
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) =>
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add("opacity-100", "translate-y-0");
            e.target.classList.remove("opacity-0", "translate-y-10");
          }
        }),
      { threshold: 0.15 },
    );
    document.querySelectorAll(".reveal").forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  return (
    <div className="bg-white overflow-hidden  pt-4">
      {/* ── HERO ── */}
      <section className="relative lg:pb-12 overflow-hidden bg-white mt-18">
        {/* Animated Particles Background */}
        <div className="absolute inset-0 pointer-events-none">
          {Array.from({ length: 15 }).map((_, i) => (
            <div
              key={i}
              className="absolute bg-primary/5 rounded-full animate-pulse-glow"
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
                width: `${Math.random() * 10 + 5}px`,
                height: `${Math.random() * 10 + 5}px`,
                animationDelay: `${Math.random() * 5}s`,
                animationDuration: `${3 + Math.random() * 5}s`,
              }}
            />
          ))}
        </div>

        <div className="max-w-7xl mx-auto px-6 relative z-10   ">
          <div className="flex flex-col lg:flex-row items-center gap-12 lg:gap-16">
            <div className="flex-1 text-center lg:text-left animate-slide-up order-2 lg:order-2 ">
              <div className="inline-flex items-center gap-2 px-4 bg-primary/5 rounded-full text-primary font-bold text-xs mb-4 tracking-wide">
                <Microscope size={14} />
                <span>India's Premier Bio-fertilizers Marketplace</span>
              </div>

              <h1 className="font-display text-5xl lg:text-7xl font-extrabold text-slate-900 leading-[1.1] tracking-tight mb-2">
                Grow Your
                <br />
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary to-secondary">
                  Business With
                </span>
                <br />
                <span className="gradient-text">Harappa Biosciences</span>
              </h1>

              <div className="flex flex-wrap items-center justify-center lg:justify-start gap-4 mb-12">
                <Link
                  to="/register"
                  className="btn btn-primary btn-lg !px-10 h-14 text-base shadow-2xl shadow-primary/20"
                >
                  Start Selling Today <ArrowRight size={20} className="ml-2" />
                </Link>
                <Link
                  to="/login"
                  className="btn btn-ghost btn-lg !px-10 h-14 text-base"
                >
                  Already a Seller? Login
                </Link>
              </div>

              <div className="flex flex-wrap items-center justify-center lg:justify-start gap-x-8 gap-y-4 border-slate-100">
                {[
                  "GST Verified",
                  "Secure Payments",
                  "Pan-India Delivery",
                  "KYC Protected",
                ].map((t) => (
                  <div
                    key={t}
                    className="flex items-center gap-2 text-slate-400 font-bold text-[10px] uppercase tracking-widest"
                  >
                    <CheckCircle2 size={14} className="text-emerald-500" />
                    <span>{t}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="flex-1 order-1 lg:order-2 hidden md:block">
              <p className="text-green-950  text-lg lg:text-xl font-medium leading-relaxed mb-10 max-w-2xl lg:mx-0 mx-auto ">
                Join India's fastest-growing network of agri-input suppliers.
                Connect directly with 5,000+ organic farmers, agricultural
                universities, and sustainable farming co-operatives.
              </p>

              <img
                src="/harappa-about-banner.jpg"
                alt="Harappa bioscience Banner"
                className="w-full h-auto rounded-2xl shadow-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* ── STATS ── */}
      {/* <section className="py-11 bg-slate-50/50 relative">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 reveal opacity-0 translate-y-10 transition-all duration-1000">
            {STATS.map(({ val, label, icon: Icon }) => (
              <div
                key={label}
                className="inner-card group hover:border-primary/20 transition-all duration-500"
              >
                <div className="w-12 h-12 rounded-2xl bg-white shadow-sm flex items-center justify-center text-primary mb-6 group-hover:scale-110 group-hover:bg-primary group-hover:text-white transition-all duration-500">
                  <Icon size={24} />
                </div>
                <div className="font-display text-4xl font-extrabold text-slate-900 mb-2 truncate">
                  {val}
                </div>
                <div className="text-xs font-bold text-slate-400 uppercase tracking-[0.2em]">
                  {label}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section> */}

      {/* ── WHY SELL ── */}
      <section className="py-12 lg:py-12" id="why-sell">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-20 reveal opacity-0 translate-y-10 transition-all duration-1000">
            <div className="inline-flex items-center gap-2 px-3 py-1 bg-amber-50 rounded-full text-amber-600 font-bold text-[10px] uppercase tracking-widest mb-4">
              <Zap size={14} />
              <span>Why Sell on Harappa?</span>
            </div>
            <h2 className="font-display text-4xl lg:text-5xl font-extrabold text-slate-900 tracking-tight mb-6">
              Everything You Need to Scale
            </h2>
            <p className="text-black  text-lg max-w-2xl mx-auto font-medium">
              From instant GST verification to pan-India logistics, we handle
              the complexity so you can focus on your science.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 ">
            {BENEFITS.map(({ icon: Icon, title, desc, color, bg }, idx) => (
              <div
                key={title}
                className="bg-green-950 inner-card p-10 group reveal opacity-0 translate-y-10 transition-all duration-1000"
                style={{ transitionDelay: `${idx * 100}ms` }}
              >
                <div
                  className={`w-14 h-14 rounded-2xl ${bg} flex items-center justify-center mb-8 border border-white transition-all duration-500 group-hover:scale-110 group-hover:shadow-lg`}
                >
                  <Icon size={28} className={color} />
                </div>
                <h3 className="text-xl font-black text-white tracking-tight mb-4 group-hover:text-yellow-800 transition-colors">
                  {title}
                </h3>
                <p className="text-white  text-sm leading-relaxed font-medium">
                  {desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── HOW IT WORKS ── */}
      <section className="py-14 lg:py-12 bg-primary relative overflow-hidden">
        <div className="absolute top-0 right-0 w-96 h-96 bg-white/5 rounded-full -mr-20 -mt-20 blur-3xl pointer-events-none" />
        <div className="absolute bottom-0 left-0 w-64 h-64 bg-white/5 rounded-full -ml-32 -mb-32 blur-3xl pointer-events-none" />

        <div className="max-w-7xl mx-auto px-6 relative z-10">
          <div className="text-center mb-20 reveal opacity-0 translate-y-10 transition-all duration-1000">
            <div className="inline-flex items-center gap-2 px-3 py-1 bg-white/10 rounded-full text-white font-bold text-[10px] uppercase tracking-widest mb-4">
              <CheckCircle2 size={14} />
              <span>Simple Onboarding</span>
            </div>
            <h2 className="font-display text-4xl lg:text-5xl font-extrabold text-white tracking-tight mb-6">
              Go Live in 4 Easy Steps
            </h2>
            <p className="text-white/60 text-lg max-w-2xl mx-auto font-medium">
              Start selling in under 30 minutes with our streamlined
              verification process.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-12 relative reveal opacity-0 translate-y-10 transition-all duration-1000">
            {STEPS.map(({ num, title, desc }, idx) => (
              <div
                key={num}
                className="relative flex flex-col items-center lg:items-start group"
              >
                <div className="text-6xl font-black text-white mb-6 group-hover:text-white/20 transition-all duration-500 font-display">
                  {num}
                </div>
                <h4 className="text-xl font-extrabold text-white mb-3 tracking-tight">
                  {title}
                </h4>
                <p className="text-white/60 text-sm leading-relaxed font-medium text-center lg:text-left">
                  {desc}
                </p>
                {idx < STEPS?.length - 1 && (
                  <ArrowRight className="absolute top-8 -right-6 text-white/10 w-8 h-8 hidden lg:block" />
                )}
              </div>
            ))}
          </div>

          <div className="mt-20 text-center reveal opacity-0 translate-y-10 transition-all duration-1000">
            <Link
              to="/register"
              className="btn bg-white text-primary hover:bg-slate-50 !px-12 h-14 text-base font-black shadow-2xl shadow-black/20"
            >
              Get Started Now <ArrowRight size={20} className="ml-2" />
            </Link>
          </div>
        </div>
      </section>

      {/* ── TESTIMONIALS ── */}
      <section className="py-14 lg:py-12">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-20 reveal opacity-0 translate-y-10 transition-all duration-1000">
            <div className="inline-flex items-center gap-2 px-3 py-1 bg-slate-100 rounded-full text-black  font-bold text-[10px] uppercase tracking-widest mb-4">
              <Star size={14} />
              <span>Seller Stories</span>
            </div>
            <h2 className="font-display text-4xl lg:text-5xl font-extrabold text-slate-900 tracking-tight mb-6">
              Trusted by Industry Leaders
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {TESTIMONIALS.map(({ name, role, quote, rating }, idx) => (
              <div
                key={name}
                className="inner-card !p-10 flex flex-col reveal opacity-0 translate-y-10 transition-all duration-1000"
                style={{ transitionDelay: `${idx * 150}ms` }}
              >
                <StarRating count={rating} />
                <p className="text-slate-600 text-lg font-bold leading-relaxed mb-10 grow tracking-tight">
                  "{quote}"
                </p>
                <div className="flex items-center gap-4 pt-6 border-t border-slate-50">
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-primary to-secondary text-white flex items-center justify-center font-black text-xl">
                    {name[0]}
                  </div>
                  <div>
                    <div className="text-sm font-black text-slate-900">
                      {name}
                    </div>
                    <div className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mt-0.5">
                      {role}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA BANNER ── */}
      <section className="pb-14 animate-slide-up">
        <div className="max-w-7xl mx-auto px-6">
          <div className="relative overflow-hidden bg-primary px-8 py-20 rounded-[40px] text-center shadow-2xl shadow-primary/30">
            <div className="absolute inset-0 bg-gradient-to-br from-primary via-primary to-secondary opacity-50" />
            <div className="absolute -top-24 -right-24 w-96 h-96 bg-white/5 rounded-full blur-3xl" />
            <div className="absolute -bottom-24 -left-24 w-96 h-96 bg-white/5 rounded-full blur-3xl" />

            <div className="relative z-10 max-w-4xl mx-auto">
              <h2 className="font-display text-3xl lg:text-4xl font-extrabold text-white leading-tight mb-6 tracking-tight">
                Become a trusted seller on Harappa Biosciences and grow your
                business today!
              </h2>
              <p className="text-white/60 text-lg font-medium mb-12">
                Register now to access 50,000+ buyers and enjoy seamless selling
                with our end-to-end support.
              </p>
              <div className="flex flex-wrap justify-center gap-4">
                <Link
                  to="/register"
                  className="btn bg-white text-primary hover:bg-slate-50 !px-12 h-14 text-base font-black shadow-xl shadow-black/20"
                >
                  Create Seller Account{" "}
                  <ArrowRight size={20} className="ml-2" />
                </Link>
                <Link
                  to="/login"
                  className="btn btn-ghost !border-white/30 !text-white hover:!bg-white/10 !px-12 h-14 text-base"
                >
                  Have an Account? Login
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── FOOTER ── */}
      <footer className="pt-14 pb-12 bg-green-950 ">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-12 mb-20">
            <div className="md:col-span-2">
              <div className="flex items-center gap-3 mb-6">
                <span className="font-bold text-xl text-white tracking-tight">
                  Harappa Biosciences
                </span>
              </div>
              <p className="text-white  text-sm font-medium leading-relaxed max-w-sm">
                India's trusted marketplace for bioscience products. Empowering
                sellers with cutting-edge tools and a vast buyer network.
              </p>
            </div>

            <div className="flex flex-col gap-6">
              <span className="text-[10px] font-bold text-white uppercase tracking-[0.2em] leading-none mb-2">
                Seller Hub
              </span>
              <div className="flex flex-col gap-3">
                <Link
                  to="/register"
                  className="text-sm font-bold text-white  hover:text-red-900 transition-colors"
                >
                  Register
                </Link>
                <Link
                  to="/login"
                  className="text-sm font-bold text-white  hover:text-red-900 transition-colors"
                >
                  Login
                </Link>
                <Link
                  to="/kyc"
                  className="text-sm font-bold text-white  hover:text-red-900 transition-colors"
                >
                  KYC Verification
                </Link>
              </div>
            </div>

            <div className="flex flex-col gap-6">
              <span className="text-[10px] font-bold text-white uppercase tracking-[0.2em] leading-none mb-2">
                Company
              </span>
              <div className="flex flex-col gap-3">
                <a
                  href="#why-sell"
                  className="text-sm font-bold text-white  hover:text-red-900 transition-colors"
                >
                  Why Sell?
                </a>
                <a
                  href="/about"
                  className="text-sm font-bold text-white  hover:text-red-900 transition-colors"
                >
                  About Us
                </a>
                <a
                  href="#"
                  className="text-sm font-bold text-white  hover:text-red-900 transition-colors"
                >
                  Contact Support
                </a>
              </div>
            </div>
          </div>

          <div className="pt-10 border-t border-slate-200 flex flex-col sm:flex-row justify-between items-center gap-6">
            <span className="text-xs font-bold text-slate-400 uppercase tracking-widest">
              © 2026 Harappa Biosciences. All rights reserved.
            </span>
            <div className="flex items-center gap-2 text-xs font-bold text-slate-400 uppercase tracking-widest">
              <span>Made with</span>
              <HeartHandshake size={14} className="text-rose-500" />
              <span>in India</span>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default HomePage;
